import { CAPABILITY_GROUPS, type GatewayComponentGroup } from "@/types/api";

/**
 * Shared knowledge about catalog components, held once for the whole GUI.
 *
 * Group ordering and the union itself live in types/api.ts (they mirror the
 * backend contract); what belongs here is everything the pages had each
 * grown their own copy of — the operator-facing group labels and the ECR
 * image URI shape. Three pages parsed image URIs with their own regex; the
 * day the registry gains a second region or a different URI form, one of
 * them would have kept working and the others would have silently shown
 * "—" or dropped the value on save.
 */

/** Operator-facing group names. Business vocabulary, no backend wording. */
export const GROUP_LABEL: Record<GatewayComponentGroup, string> = {
  core: "Core",
  inputs: "Inputs",
  outputs: "Outputs",
  widgets: "Widgets",
  features: "Features",
};

/**
 * Whether a group's components can advertise a capability. Predicate rather
 * than an inline ``includes`` so the widening stays in one place — and so
 * the question is asked the same way everywhere it is asked.
 */
export function isCapabilityGroup(group: GatewayComponentGroup): boolean {
  return (CAPABILITY_GROUPS as readonly GatewayComponentGroup[]).includes(group);
}

/**
 * ``<account>.dkr.ecr.<region>.amazonaws.com/<repository>`` — same shape as
 * ``_ECR_URI_RE`` in lambda-layers/control_plane_layer/python/catalog_compose.py.
 * The two sides can't share a definition, but each side holds exactly one.
 */
const ECR_URI_RE = /^(\d{12}\.dkr\.ecr\.[a-z0-9-]+\.amazonaws\.com)\/(.+)$/;

/**
 * Split an image URI into its registry prefix and its repository name, or
 * null when it isn't ECR-shaped. The prefix is preserved rather than
 * rebuilt: the catalogue still carries a legacy registry account on most of
 * its images, and rewriting it here would change what the dataplane pulls.
 */
export function splitImageUri(
  uri: string,
): { prefix: string; repository: string } | null {
  const match = ECR_URI_RE.exec(uri);
  return match ? { prefix: match[1], repository: match[2] } : null;
}

/**
 * The repository name alone, for display — never the account/region prefix,
 * which is noise in a table. Falls back to the raw value when the URI isn't
 * ECR-shaped, so nothing is silently hidden.
 */
export function repositoryNameOf(uri: unknown): string | null {
  if (typeof uri !== "string" || !uri) return null;
  return splitImageUri(uri)?.repository ?? uri;
}
